import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { IconButton } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { useRecentAnalysis } from "app/custom-hooks/react-query/useRecentAnalysis";
import { translate } from "app/filters/i18nFilter";
import { LLMDomainAutocomplete } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMDomainAutocomplete";
import type { IAiTrackerCampaign } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { AssetsService } from "app/services/AssetsService";
import type { IWebsiteItem } from "pro-features/components/AutocompleteWebsites/types";

import { DemoBox } from "./DemoBox";

const BackgroundWrapper = styled(Flex)`
    background-image: url(${AssetsService.assetUrl("/images/llm-tracker/Homepage-llm.svg")});
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    min-height: 100%;
    width: 100%;
`;

interface ILLMEmptyStateHomepageProps {
    title: string;
    subtitle: string;
    autocompleteTitle: string;
    campaigns: IAiTrackerCampaign[];
    selectedDomain: IWebsiteItem;
    onDomainChange: (domain: IWebsiteItem) => void;
    onCreateCampaign: () => void;
    onDemoCampaignClick: (campaign: IAiTrackerCampaign) => void;
    isCreationAllowed: boolean;
}

export const LLMEmptyStateHomepage = ({
    title,
    subtitle,
    autocompleteTitle,
    campaigns,
    selectedDomain,
    onDomainChange,
    onCreateCampaign,
    onDemoCampaignClick,
    isCreationAllowed,
}: ILLMEmptyStateHomepageProps) => {
    const exampleDemoCampaign = campaigns.find((campaign) => campaign.isDemo);
    const { data: recentItems, isLoading: isFetching } = useRecentAnalysis();

    return (
        <BackgroundWrapper
            flexDirection="column"
            alignItems="center"
            data-automation="llm-homepage-container"
        >
            <Text
                fontVariant="h2"
                color={colorsPalettes.carbon[400]}
                margin="128px 0 0"
                data-automation="llm-homepage-title"
            >
                {title}
            </Text>
            <Text
                fontVariant="body1"
                color={colorsPalettes.carbon[400]}
                margin="30px 0 0"
                maxWidth="763px"
                data-automation="llm-homepage-subtitle"
                textAlign="center"
            >
                {subtitle}
            </Text>
            <Text
                fontVariant="report"
                color={colorsPalettes.carbon[400]}
                margin="80px 0 0"
                data-automation="llm-homepage-autocomplete-title"
            >
                {autocompleteTitle}
            </Text>
            <Flex
                gap="8px"
                alignItems="center"
                margin="12px 0 0"
            >
                <LLMDomainAutocomplete
                    selectedDomain={selectedDomain}
                    setSelectedDomain={onDomainChange}
                    recentItems={recentItems}
                    isFetching={isFetching}
                    placeholder={translate("llm.homepage.autocomplete.placeholder")}
                />
                <IconButton
                    type={isCreationAllowed ? "primary" : "trial"}
                    iconName="sparkles-ai"
                    iconColor={colorsPalettes.carbon[0]}
                    onClick={onCreateCampaign}
                    isDisabled={!selectedDomain}
                    data-automation="llm-homepage-ai-button"
                >
                    {translate("llm.homepage.ai.track.button")}
                </IconButton>
            </Flex>
            <Text
                fontVariant="button"
                color={colorsPalettes.carbon[250]}
                margin="80px 0 0"
                data-automation="llm-homepage-demo-title"
            >
                {translate("llm.homepage.demo.title")}
            </Text>
            <DemoBox
                campaign={exampleDemoCampaign}
                onDemoCampaignClick={onDemoCampaignClick}
            />
        </BackgroundWrapper>
    );
};
